import React, { useEffect, useRef } from 'react';

// Renders the Korsa glasses glTF with three.js (loaded from CDN) and plays the
// model's intro animation once. Positioning comes from `className`; the canvas
// fills whatever wrapper it is given.
export const GlassesModel = ({ modelPath = '/models/model.glb', className = '' }) => {
  const wrapperRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    let renderer, scene, fallbackCamera, mixer, model, canvas, rafId;
    let activeCamera = null;
    let onResize;

    const loadScript = (src) => new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = res;
      s.onerror = rej;
      document.head.appendChild(s);
    });

    const cleanup = () => {
      mounted = false;
      if (onResize) window.removeEventListener('resize', onResize);
      if (rafId) cancelAnimationFrame(rafId);
      if (scene && model) {
        scene.remove(model);
        if (model.traverse) {
          model.traverse(node => {
            if (node.geometry) node.geometry.dispose();
            if (node.material) {
              if (Array.isArray(node.material)) {
                node.material.forEach(mat => mat.dispose());
              } else {
                node.material.dispose();
              }
            }
          });
        }
      }
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
      }
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };

    cleanupRef.current = cleanup;

    (async () => {
      try {
        if (!window.THREE) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js');
        if (!window.THREE.GLTFLoader) await loadScript('https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js');

        if (!mounted) return;
        const THREE = window.THREE;
        const GLTFLoader = THREE.GLTFLoader;

        const wrapper = wrapperRef.current;
        if (!wrapper || !mounted) return;

        canvas = document.createElement('canvas');
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        wrapper.appendChild(canvas);

        renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
        const isMobileInit = window.innerWidth < 768;
        renderer.setPixelRatio(isMobileInit ? 1.0 : window.devicePixelRatio || 1);
        renderer.setSize(wrapper.clientWidth, wrapper.clientHeight, false);
        renderer.setClearColor(0x000000, 0);
        renderer.outputEncoding = THREE.sRGBEncoding;

        scene = new THREE.Scene();

        fallbackCamera = new THREE.PerspectiveCamera(35, wrapper.clientWidth / wrapper.clientHeight, 0.1, 1000);
        fallbackCamera.position.set(0, 0, 5);
        activeCamera = fallbackCamera;

        const ambient = new THREE.AmbientLight(0xffffff, 0.02);
        scene.add(ambient);
        const dir1 = new THREE.DirectionalLight(0xffffff, 0.03);
        dir1.position.set(-200, 200, 200);
        scene.add(dir1);

        const loader = new GLTFLoader();
        loader.load(
          modelPath,
          (gltf) => {
            if (!mounted) return;
            model = gltf.scene;
            scene.add(model);

            if (gltf.cameras && gltf.cameras.length > 0) {
              activeCamera = gltf.cameras[0];
              activeCamera.aspect = wrapper.clientWidth / wrapper.clientHeight;
              activeCamera.updateProjectionMatrix();
            }

            if (gltf.animations && gltf.animations.length > 0) {
              mixer = new THREE.AnimationMixer(model);
              let maxDuration = 0;

              gltf.animations.forEach(clip => {
                const action = mixer.clipAction(clip);
                action.loop = THREE.LoopOnce;
                action.clampWhenFinished = true;
                action.play();
                maxDuration = Math.max(maxDuration, clip.duration);
              });

              mixer.__duration = maxDuration;
            }

            if (renderer && scene && activeCamera) {
              renderer.render(scene, activeCamera);
            }
          },
          undefined,
          (err) => console.error('Error loading model:', err)
        );

        onResize = () => {
          if (!mounted || !wrapper || !renderer || !activeCamera) return;
          renderer.setSize(wrapper.clientWidth, wrapper.clientHeight, false);
          activeCamera.aspect = wrapper.clientWidth / wrapper.clientHeight;
          activeCamera.updateProjectionMatrix();
        };

        const clock = new THREE.Clock();
        const renderLoop = () => {
          if (!mounted) return;

          // Play the model's intro animation once on load, then hold the final frame
          if (mixer) mixer.update(clock.getDelta());

          if (mounted && renderer && scene && activeCamera) {
            renderer.render(scene, activeCamera);
            rafId = requestAnimationFrame(renderLoop);
          }
        };

        window.addEventListener('resize', onResize);
        renderLoop();
      } catch (err) {
        console.error('Three.js Init Error:', err);
      }
    })();

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [modelPath]);

  return <div ref={wrapperRef} className={className} />;
};
